import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { FavoriteService } from '../../services';
import { queryKeys } from '../keys';
import type { FavoriteDto, FavoriteTargetType, ToggleFavoriteRequest } from '../../domain';

export function useFavorites(targetType?: FavoriteTargetType) {
  return useQuery({
    queryKey: queryKeys.favorites(targetType),
    queryFn: () => FavoriteService.list(targetType),
  });
}

/**
 * Optimistic favorite toggle. On click we immediately flip the cached
 * `favorites(targetType)` list (add/remove the target) so the heart reacts
 * instantly; on error we roll back to the snapshot, and either way we
 * re-sync from the server on settle. Also invalidates the `assets` bucket so
 * a `{ favorited: true }` filtered grid reflects the change.
 */
export function useToggleFavorite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: ToggleFavoriteRequest) => FavoriteService.toggle(request),
    onMutate: async ({ targetType, targetId }: ToggleFavoriteRequest) => {
      const key = queryKeys.favorites(targetType);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<FavoriteDto[]>(key);

      qc.setQueryData<FavoriteDto[]>(key, (current) => {
        const list = current ?? [];
        const exists = list.some((f) => f.targetId === targetId);
        if (exists) {
          return list.filter((f) => f.targetId !== targetId);
        }
        const optimistic: FavoriteDto = {
          userId: '',
          targetType,
          targetId,
          favoritedAt: new Date().toISOString(),
        };
        return [optimistic, ...list];
      });

      return { key, previous };
    },
    onError: (_err, _vars, context) => {
      if (context) {
        qc.setQueryData(context.key, context.previous);
      }
    },
    onSettled: (_data, _err, { targetType }) => {
      qc.invalidateQueries({ queryKey: queryKeys.favorites(targetType) });
      qc.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}
